"use client"

import { CompletedCollectible, DraftCollectible } from "@/types/collectibles"
import { SimulateContractReturnType } from "viem"
import { useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Loader2, LucideCircleCheck } from "lucide-react"
import { cn } from "@/lib/utils"

import { UseWaitForTransactionReceiptReturnType } from "wagmi"
import { postCollectButtonClicked } from "@/app/(app)/(paragraph)/home/tracking"
import MintPriceText from "@/app/@modal/[blogSlug]/(.)nft/components/MintPriceText"
import { usePromiseRouter } from "@/app/hooks/usePromiseRouter"
import { toast } from "sonner"
import { useAtom } from "jotai"
import { userJustMintedAtom } from "@/app/contexts/Atoms"
import { useMintCollectible } from "@/app/@modal/[blogSlug]/(.)nft/hooks/useMintCollectible"
import useUserHaveToken from "@/app/@modal/[blogSlug]/(.)nft/hooks/useUserHaveToken"

export default function MintBtnReady({
  collectible,
  simulateRequest,
  onTxHashCallback,
  onTxReceiptCallback,
  className,
  isFirstMint,
}: {
  collectible: CompletedCollectible | DraftCollectible
  simulateRequest: SimulateContractReturnType
  /**
   * Callback to be called when txHash is available. This does NOT mean the transaction is successful; just that it was submitted.
   */
  onTxHashCallback?: (txHash: string) => void

  className?: string

  /** Boolean value indicating if this is the first mint for a collectible */
  isFirstMint?: boolean

  /**
   * Callback to be called when txReceipt is available. This means the transaction was successfully confirmed on the blockchain.
   */
  onTxReceiptCallback?: (
    txReceipt: UseWaitForTransactionReceiptReturnType
  ) => Promise<void>
}) {
  const { onClick, txReceipt, txHash } = useMintCollectible({
    chain: collectible.chain,
    simulateRequest,
  })
  const router = usePromiseRouter()

  const { hasToken } = useUserHaveToken(collectible)

  // If undefined, the user has not minted yet
  // If false, the user has minted but the transaction is not yet confirmed
  // If true, the user has minted and the transaction is confirmed
  const [userJustMinted, setUserJustMinted] = useAtom(userJustMintedAtom)

  useEffect(() => {
    if (!txHash) return

    console.log("Txn hash received", txHash, " now running callback")

    onTxHashCallback?.(txHash)
  }, [txHash])

  useEffect(() => {
    console.log("Txn Receipt", txReceipt)
    if (!txReceipt.isSuccess) return

    // Run the callback and then refresh the server components
    const refresh = async () => {
      // Set to false before awaiting. Only once it's true do
      // we show the "Minted! Mint again?" message
      setUserJustMinted(false)
      await onTxReceiptCallback?.(txReceipt)
      toast.success("Minted successfully!", {
        icon: <LucideCircleCheck className="text-green-500 w-5 h-5 mr-2" />,
      })
      setUserJustMinted(true)

      // When this runs, if this is the FIRST time the NFT is collected,
      // this will cause the MintBtnReady component to get destroyed and re-created,
      // since it follows two different render paths in SimulateTxn.tsx.
      await router.refresh()
    }

    refresh()
  }, [txReceipt.isSuccess])

  const isPending = txReceipt.isLoading || userJustMinted === false

  const children = isPending ? (
    <Loader2 className="animate-spin mr-2" size={16} />
  ) : userJustMinted ? (
    "Minted! Mint again?"
  ) : hasToken ? (
    "Mint Again"
  ) : (
    <MintPriceText collectible={collectible} />
  )

  return (
    <Button
      disabled={isPending}
      onClick={() => {
        postCollectButtonClicked(collectible, isFirstMint)
        onClick()
      }}
      className={cn(
        "w-full disabled:opacity-50 disabled:cursor-not-allowed",
        className
      )}
    >
      {children}
    </Button>
  )
}
